import { Stack, ToggleButton, ToggleButtonGroup } from '@mui/material'
import React from 'react'

export interface AnswerPanelTab<T extends string> {
  value: T
  label: React.ReactNode
  icon?: React.ReactNode
  tooltip?: string
  /** Shown with its label but not selectable: there is nothing to render under it. */
  disabled?: boolean
}

interface AnswerPanelTabsProps<T extends string> {
  value: T | undefined
  tabs: AnswerPanelTab<T>[]
  onChange: (value: T) => void
}

/** Borderless pills; the selected one is filled grey. Replaces the underlined tabs under an AI answer. */
export const AnswerPanelTabs = <T extends string>({
  value,
  tabs,
  onChange,
}: AnswerPanelTabsProps<T>): React.ReactElement => (
  <ToggleButtonGroup
    exclusive
    size="small"
    value={value ?? null}
    onChange={(_, next: T | null) => next !== null && onChange(next)}
    sx={{
      gap: 1,
      '& .MuiToggleButton-root': {
        border: 0,
        height: 32,
        borderRadius: 1,
        color: 'text.primary',
        textTransform: 'none',
        px: 1.5,
        py: 0,
        '&.MuiToggleButtonGroup-grouped': { borderRadius: 1, m: 0 },
      },
      '& .MuiToggleButton-root.Mui-selected': {
        bgcolor: 'grey.300',
        '&:hover': { bgcolor: 'grey.300' },
      },
      '& .MuiToggleButton-root.Mui-disabled': { border: 0, color: 'text.primary' },
    }}
  >
    {tabs.map(tab => (
      <ToggleButton
        key={tab.value}
        value={tab.value}
        disabled={tab.disabled}
        title={tab.tooltip}
        data-testid={`answer-tab-${tab.value}`}
      >
        <Stack direction="row" alignItems="center" gap={1}>
          {tab.icon}
          {tab.label}
        </Stack>
      </ToggleButton>
    ))}
  </ToggleButtonGroup>
)
